/*
 * Phase 5 exit criteria: the whole page fits 1440×900 without the document
 * scrolling. `height: 100vh` + `overflow: hidden` on the root is the hard
 * guarantee.
 *
 * Below 900px, recent activity (section 3) is the *only* section allowed to
 * shrink below its natural content size (`flex-shrink: 1`, and its own
 * `overflow: hidden` clips rather than growing the box) — it's a five-line
 * preview with a "see all" escape hatch, the section costing the least to
 * lose. Metrics and the attention queue both get `flex-shrink: 0`: they
 * never concede space, so a shrink-*ratio* difference (which only changes
 * how much each section shrinks, not whether it shrinks at all) can't leave
 * the queue — the reason the operator is here — clipped alongside recent
 * activity. Extra height beyond everyone's natural size goes to the
 * attention queue (`flex-grow: 1`), matching UI spec §2's ~50% share.
 */

.page {
  /*
   * `100%`, not `100vh` — `LGH-016`. The shell (`RequireAuth`) owns the
   * viewport and renders the workspace block above this, so `100vh` here
   * meant `chrome + 100vh` and the document scrolled on every viewport.
   * `100%` is the remainder the shell hands down, and it is a definite
   * height because the route area is a flex item with `min-height: 0`.
   *
   * `overflow: hidden` stays: this page still clips rather than scrolls,
   * which is the guarantee below. Only the box it measures against changed.
   */
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 2rem;
  gap: 1rem;
  box-sizing: border-box;
}

.metrics {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

/*
 * `LGH-028`. The Phase 5 exit criterion above is pinned to 1440×900 by its own
 * comment — it was never a claim about every viewport, only the one a design
 * partner demo runs at. Below this width the same fixed, clipping shell would
 * hide whichever section drew the short straw with no way to reach the rest
 * of it, which is a worse failure than the page simply scrolling. `.attention`
 * keeps its own internal scroll either way, so this does not touch how the
 * queue itself behaves — only how the three sections share a viewport that
 * can no longer fit all of them un-scrolled.
 *
 * UNVERIFIED LIVE — `LGH-028`'s own report says so. Reasoned from this file
 * and `HomePage.tsx` with no live render at this width to check against;
 * revisit the moment the ladder's `mobile` project can actually run.
 */
@media (max-width: 720px) {
  .page {
    overflow-y: auto;
    height: auto;
    min-height: 100%;
  }

  .metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

.attention {
  flex: 1 0 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.recent {
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/*
 * The clipped part of the section, and only this part. The header above it
 * holds the [See all runs →] escape hatch that justifies letting this
 * section give way first — so the header never shrinks and never clips.
 */
.recentRows {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.sectionHeader {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  /* `LGH-028`. Both header rows (identity + Ask/Health/Sign out; title +
     as-of) tolerate a second line far better than the trailing item —
     Sign out, the as-of line — being clipped by `.page`'s `overflow: hidden`
     with no way to reach it. */
  flex-wrap: wrap;
  row-gap: 0.35rem;
}

.sectionTitle {
  font-family: var(--font-display);
  font-size: 1.1rem;
}

.count {
  font-family: var(--font-mono);
  font-size: var(--text-min-mono);
  color: var(--muted);
}

.emptyQueue {
  color: var(--muted);
  font-family: var(--font-mono);
  font-size: var(--text-min-mono);
}

.more {
  font-family: var(--font-mono);
  font-size: var(--text-min-mono);
  color: var(--muted);
  margin-top: 0.5rem;
}

.recentRow {
  display: flex;
  gap: 0.75rem;
  align-items: baseline;
  padding: 0.3rem 0;
  font-family: var(--font-mono);
  font-size: var(--text-min-mono);
  border-bottom: 1px solid var(--rule);
  cursor: pointer;
}

.recentRow:hover {
  background: var(--card);
}
